/**
 * Print a standalone document (receipt, KOT, void slip, reading).
 *
 * Every print path used to be `window.open('', '_blank')` followed straight by
 * `printWindow.document.write(...)`, with no null check. `window.open` returns
 * null whenever the popup is refused -- a blocker, an enterprise policy, a
 * kiosk profile -- and the next line then threw
 * `TypeError: Cannot read properties of null`, so the button did nothing at
 * all: no print, no message, no way for the cashier to know why. That is the
 * "unable to print" the client reported, reproduced from a real click.
 *
 * A same-document iframe needs no popup permission, so it works where the
 * popup is refused. The popup is kept only as a fallback, and if BOTH are
 * refused the caller is told rather than left guessing.
 *
 * Returns true if a print was started.
 */
export function printDocument({ title = '', css = '', body = '' } = {}) {
  return printHtml(
    '<!doctype html><html><head><meta charset="utf-8">' +
    `<title>${title}</title><style>${css}</style></head>` +
    `<body>${body}</body></html>`
  );
}

/** Same, for a caller that has already assembled the whole document. */
export function printHtml(html) {
  return printViaIframe(html) || printViaPopup(html);
}

/**
 * The message to show when printing could not be started. Kept here so every
 * print button says the same thing and names the actual remedy.
 */
export const PRINT_BLOCKED_MESSAGE =
  'Could not open the print view. Allow pop-ups for this site in your browser, then try again.';

function printViaIframe(html) {
  let frame = null;
  try {
    frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    // Off-screen rather than display:none -- a frame that is not rendered has
    // no layout, and some engines then print a blank page.
    frame.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none';
    document.body.appendChild(frame);

    const win = frame.contentWindow;
    if (!win) throw new Error('iframe has no contentWindow');

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      // Only after the print dialog has been dismissed -- removing the frame
      // while it is open cancels the job.
      setTimeout(() => frame && frame.remove(), 500);
    };

    const start = () => {
      try {
        win.focus();
        win.print();
      } finally {
        cleanup();
      }
    };

    win.addEventListener('afterprint', cleanup);
    // Belt and braces: afterprint does not fire everywhere.
    setTimeout(cleanup, 60000);

    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();

    if (doc.readyState === 'complete') start();
    else win.addEventListener('load', start, { once: true });

    return true;
  } catch (err) {
    console.error('iframe print failed, falling back to popup:', err);
    if (frame) frame.remove();
    return false;
  }
}

function printViaPopup(html) {
  const win = window.open('', '_blank');
  if (!win) return false; // the null that used to throw one line later
  try {
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
    return true;
  } catch (err) {
    console.error('popup print failed:', err);
    return false;
  }
}
